import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import pccarelogo from "../assests/images/pccarelogo.jpg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { buildWhatsAppLink } from "../utils/whatsapp";
import { getRequest } from "../apis/ApiInstence";

const mapUrlJabalpur =
  "https://www.google.com/maps/search/?api=1&query=Pc+Care+Computer+Laptop+Repair+Center+23.168639,79.931806";
const mapUrlBhopal =
  "https://www.google.com/maps/search/?api=1&query=2nd+Floor,+80-B,+Kasturba+Nagar,+Near+Chetak+Bridge,+Bhopal,+Madhya+Pradesh+462023";
const ROTATING_PLACEHOLDERS = [
  "Search: best laptop",
  "Search: i5 16GB SSD",
  "Search: Dell, HP, Lenovo",
  "Search: gaming laptop",
  "Search: new arrivals",
];

const TypewriterPlaceholder = React.memo(({ searchTerm, searchFocused }) => {
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [displayedPlaceholder, setDisplayedPlaceholder] = useState("");
  const [isDeletingPlaceholder, setIsDeletingPlaceholder] = useState(false);

  useEffect(() => {
    if (searchTerm) return undefined;

    const currentText = ROTATING_PLACEHOLDERS[placeholderIndex] || "";
    let delay = isDeletingPlaceholder ? 40 : 65;

    if (!isDeletingPlaceholder && displayedPlaceholder.length === currentText.length) {
      delay = 1100;
    } else if (isDeletingPlaceholder && displayedPlaceholder.length === 0) {
      delay = 260;
    }

    const timer = setTimeout(() => {
      if (!isDeletingPlaceholder && displayedPlaceholder.length < currentText.length) {
        setDisplayedPlaceholder(currentText.slice(0, displayedPlaceholder.length + 1));
        return;
      }
      if (!isDeletingPlaceholder && displayedPlaceholder.length === currentText.length) {
        setIsDeletingPlaceholder(true);
        return;
      }
      if (isDeletingPlaceholder && displayedPlaceholder.length > 0) {
        setDisplayedPlaceholder(currentText.slice(0, displayedPlaceholder.length - 1));
        return;
      }
      setIsDeletingPlaceholder(false);
      setPlaceholderIndex((prev) => (prev + 1) % ROTATING_PLACEHOLDERS.length);
    }, delay);

    return () => clearTimeout(timer);
  }, [placeholderIndex, displayedPlaceholder, isDeletingPlaceholder, searchTerm]);

  if (searchTerm || searchFocused) return null;

  return (
    <span className="pc-placeholder-hint">
      {displayedPlaceholder}
      <span className="pc-placeholder-caret">|</span>
    </span>
  );
});

const Navbar = () => {



const navigate = useNavigate();
const location = useLocation();
const query = new URLSearchParams(location.search).get("q");
const isPathActive = (path) =>
  location.pathname === path || location.pathname.startsWith(`${path}/`);
const getNavColor = (path) => (isPathActive(path) ? "#F3802C" : "#351F72");
const getNavWeight = (path) => (isPathActive(path) ? 700 : 600);
const policyRoutes = ["/shipping-policy", "/return-policy", "/refund-policy", "/refund-cancellation-policy", "/warranty-policy", "/privacy-policy"];
const otherRoutes = ["/faq", "/terms-conditions"];
const isPolicyActive = policyRoutes.some((route) => isPathActive(route));
const isOtherActive = otherRoutes.some((route) => isPathActive(route));

const enquiryWhatsAppLink = buildWhatsAppLink({
  text: `Hello, I want to enquire about PC Care.\nPage: ${
    typeof window !== "undefined" ? window.location.href : ""
  }`,
});

  const getBackendRoot = () => {
    const raw =
      process.env.REACT_APP_BACKEND ||
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      "http://localhost:8000";
    const trimmed = String(raw).replace(/\/$/, "");
    return trimmed.replace(/\/api\/v1$/i, "");
  };

  const assetBaseUrl = useMemo(() => getBackendRoot(), []);

  const parseImages = useCallback((rawImages) => {
    if (!rawImages) return [];
    if (Array.isArray(rawImages)) return rawImages.filter(Boolean).map(String);
    if (typeof rawImages === "string") {
      const value = rawImages.trim();
      if (!value) return [];
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
      } catch {
        // ignore parse errors
      }
    }
    return [];
  }, []);

  const normalizeProductImage = useCallback((value) => {
    if (!value) return "";
    const raw = String(value);
    if (/^https?:\/\//i.test(raw)) return raw;
    const cleaned = raw.replace(/^\/+/, "");
    return `${assetBaseUrl}/uploads/${encodeURIComponent(cleaned)}`;
  }, [assetBaseUrl]);

  const getProductThumb = useCallback((product) => {
    const images = parseImages(product?.images);
    if (images.length > 0) {
      const first = images[0];
      return normalizeProductImage(first);
    }

    const singleImage =
      product?.image ||
      product?.product_image ||
      product?.productImage ||
      product?.thumbnail;
    return normalizeProductImage(singleImage);
  }, [normalizeProductImage, parseImages]);

  const handleLogout = () => {
    try {
      localStorage.removeItem("user");
    } catch {
      // ignore storage errors
    }
    window.dispatchEvent(new Event("userUpdated"));
    setMobileMenu(false);
    navigate("/login");
  };
  /* ================= STATES ================= */

  const [showDropdown, setShowDropdown] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const locationRef = useRef(null);
  const headerRef = useRef(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [mobileTopHidden, setMobileTopHidden] = useState(false);
  const [mobileBottomHidden, setMobileBottomHidden] = useState(false);
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== "undefined" ? window.innerWidth < 992 : true
  );

  // Mobile dropdown states
  const [mobilePolicyOpen, setMobilePolicyOpen] = useState(false);
  const [mobileOtherOpen, setMobileOtherOpen] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchHint, setSearchHint] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchWrapRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const latestSuggestionRequestRef = useRef(0);

  const [productsIndex, setProductsIndex] = useState([]);
  const [productsIndexLoading, setProductsIndexLoading] = useState(false);
  const [productsIndexError, setProductsIndexError] = useState("");

  // Cart state
  const [cartCount, setCartCount] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);

  /* ================= CART COUNT ================= */

  const updateCartCount = () => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    setCartCount(cart.length);
  };

  useEffect(() => {
    updateCartCount();
    window.addEventListener("cartUpdated", updateCartCount);
    return () => {
      window.removeEventListener("cartUpdated", updateCartCount);
    };
  }, []);

  /* ================= USER STATE ================= */

  const refreshUser = () => {
    try {
      const saved = localStorage.getItem("user");
      setCurrentUser(saved ? JSON.parse(saved) : null);
    } catch {
      setCurrentUser(null);
    }
  };

  useEffect(() => {
    refreshUser();
    window.addEventListener("userUpdated", refreshUser);
    window.addEventListener("storage", refreshUser);
    return () => {
      window.removeEventListener("userUpdated", refreshUser);
      window.removeEventListener("storage", refreshUser);
    };
  }, []);

  const isLoggedIn = Boolean(currentUser?.id);
  const userInitial =
    currentUser?.name?.trim()?.charAt(0) ||
    currentUser?.email?.trim()?.charAt(0) ||
    currentUser?.mobile?.trim()?.charAt(0) ||
    "U";

  /* ================= SEARCH FUNCTION ================= */

 const handleSearch = (e) => {
  e.preventDefault();

  const query = searchTerm.trim();
  if (!query) {
    setSearchHint("Try: best laptop, new laptop, i5, 16GB, Dell");
    return;
  }

  setSearchHint("");
  setSuggestionsOpen(false);
  setMobileSearchOpen(false);
  navigate(`/products?q=${encodeURIComponent(query)}`);
};

  const ensureProductsIndex = useCallback(async (value = "") => {
    const queryValue = String(value || "").trim();
    const requestId = ++latestSuggestionRequestRef.current;

    if (queryValue.length < 2) {
      setProductsIndex([]);
      setProductsIndexError("");
      setProductsIndexLoading(false);
      return;
    }

    setProductsIndexLoading(true);
    setProductsIndexError("");

    try {
      const res = await getRequest("/products/search", {
        params: { query: queryValue, status: "active", limit: 8 },
      });

      const payload = res?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      const normalized = list
        .filter((p) => p && p.id && String(p.status || "active") === "active")
        .map((p) => ({
          id: p.id,
          name: String(p.name || "").trim(),
          brand: String(p.brand || "").trim(),
          processor: String(p.processor || "").trim(),
          ram: String(p.ram || "").trim(),
          ssd: String(p.ssd || "").trim(),
          generation: String(p.generation || "").trim(),
          screen_size: String(p.screen_size || "").trim(),
          image: getProductThumb(p),
        }))
        .filter((p) => p.name);

      if (requestId !== latestSuggestionRequestRef.current) return;
      setProductsIndex(normalized);
    } catch (err) {
      if (requestId !== latestSuggestionRequestRef.current) return;
      console.error("Navbar products index load error:", err);
      setProductsIndexError("Suggestions load nahi ho paaye.");
      setProductsIndex([]);
    } finally {
      if (requestId === latestSuggestionRequestRef.current) {
        setProductsIndexLoading(false);
      }
    }
  }, [getProductThumb]);

  const suggestions = useMemo(() => {
    return productsIndex.slice(0, 7);
  }, [productsIndex]);

  const openSuggestions = () => {
    setSearchFocused(true);
    setSuggestionsOpen(true);
  };

  const closeSuggestions = () => {
    setSearchFocused(false);
    setSuggestionsOpen(false);
  };

  const goToProduct = (id) => {
    setSuggestionsOpen(false);
    setMobileSearchOpen(false);
    navigate(`/products2/${id}?type=product`);
  };

  useEffect(() => {
    if (!suggestionsOpen) return;

    const onDocMouseDown = (e) => {
      const wrap = searchWrapRef.current;
      if (!wrap) return;
      if (wrap.contains(e.target)) return;
      closeSuggestions();
    };

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [suggestionsOpen]);

  useEffect(() => {
    if (!mobileSearchOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileSearchOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileSearchOpen]);

  useEffect(() => {
    if (!locationOpen) return;

    const onDocMouseDown = (e) => {
      if (locationRef.current && !locationRef.current.contains(e.target)) {
        setLocationOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [locationOpen]);

  useEffect(() => {
    if (!suggestionsOpen && !mobileSearchOpen) return undefined;

    const queryValue = searchTerm.trim();
    window.clearTimeout(searchDebounceRef.current);

    if (queryValue.length < 2) {
      ensureProductsIndex("");
      return undefined;
    }

    searchDebounceRef.current = window.setTimeout(() => {
      ensureProductsIndex(queryValue);
    }, 300);

    return () => {
      window.clearTimeout(searchDebounceRef.current);
    };
  }, [ensureProductsIndex, mobileSearchOpen, searchTerm, suggestionsOpen]);

  useEffect(() => {
    if (location.pathname === "/products") {
      setSearchTerm(query || "");
    }
  }, [location.pathname, query]);


  useEffect(() => {
    if (mobileMenu) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobileMenu]);

  useEffect(() => {
    if (!mobileSearchOpen) return;
    document.body.style.overflow = "hidden";

    const t = window.setTimeout(() => {
      try {
        mobileSearchRef.current?.focus?.();
      } catch {
        // ignore
      }
    }, 0);

    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = "auto";
    };
  }, [mobileSearchOpen]);

  useEffect(() => {
    const handleScrollAndFooter = () => {
      const isMobile = window.innerWidth < 992;
      setIsMobileView(isMobile);
      if (!isMobile) {
        setMobileTopHidden(false);
        setMobileBottomHidden(false);
        return;
      }

      const y = window.scrollY;

      const footer = document.querySelector("footer");
      let reachedFooter = false;
      if (footer) {
        const rect = footer.getBoundingClientRect();
        reachedFooter = rect.top <= window.innerHeight - 8;
      }
      setMobileBottomHidden(reachedFooter);
    };

    handleScrollAndFooter();
    window.addEventListener("scroll", handleScrollAndFooter, { passive: true });
    window.addEventListener("resize", handleScrollAndFooter);

    return () => {
      window.removeEventListener("scroll", handleScrollAndFooter);
      window.removeEventListener("resize", handleScrollAndFooter);
    };
  }, [location.pathname]);

  /* ================= HEADER HEIGHT (for fixed header spacer) ================= */

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const update = () => {
      document.documentElement.style.setProperty(
        "--app-header-h",
        `${el.offsetHeight}px`
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);


  /* ================= RETURN BELOW THIS ================= */


  return (
    <>
      <style>{`
        @keyframes iconFloat {
          0% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
          100% { transform: translateY(0); }
        }
        @keyframes drawerInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes searchGlow {
          0% { box-shadow: 0 6px 18px rgba(53, 31, 114, 0.1); }
          50% { box-shadow: 0 8px 26px rgba(243, 128, 44, 0.28); }
          100% { box-shadow: 0 6px 18px rgba(53, 31, 114, 0.1); }
        }
        .pc-icon-anim {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          animation: iconFloat 2.6s ease-in-out infinite;
          transition: transform 0.2s ease, filter 0.2s ease;
        }
        .pc-icon-anim:hover {
          transform: translateY(-3px) scale(1.06);
          filter: drop-shadow(0 4px 8px rgba(53, 31, 114, 0.35));
        }
        .pc-search-wrap {
          transition: all 0.32s ease;
          transform: translateY(0) scale(1);
        }
        .pc-search-wrap.active {
          border-color: #f3802c !important;
          animation: searchGlow 1.6s ease-in-out infinite;
          transform: translateY(-1px) scale(1.01);
        }
        .pc-search-input {
          transition: all 0.28s ease;
          font-weight: 600;
          color: #1f3c88;
          letter-spacing: 0.1px;
        }
        .pc-search-icon {
          transition: transform 0.25s ease, opacity 0.2s ease;
        }
        .pc-search-wrap.active .pc-search-icon {
          transform: scale(1.08);
        }
        .pc-search-btn {
          transition: all 0.28s ease;
        }
        .pc-search-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 10px 20px rgba(243, 128, 44, 0.3);
        }
        .pc-placeholder-hint {
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          color: #5f6b8a;
          font-size: 15px;
          font-weight: 600;
          line-height: 1;
          pointer-events: none;
          user-select: none;
          white-space: nowrap;
        }
        @keyframes caretBlink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        .pc-placeholder-caret {
          display: inline-block;
          margin-left: 2px;
          animation: caretBlink 1s step-end infinite;
        }

        @keyframes pcMobileSearchOverlayIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes pcMobileSearchBarIn {
          from { opacity: 0; transform: translateY(16px) scale(0.985); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes pcMobileSearchSuggestionsIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .pc-mobile-search-overlay {
          animation: pcMobileSearchOverlayIn 160ms ease-out both;
        }
        .pc-mobile-search-bar {
          animation: pcMobileSearchBarIn 220ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .pc-mobile-search-suggestions {
          animation: pcMobileSearchSuggestionsIn 160ms ease-out both;
        }

        input[type="search"]::-webkit-search-cancel-button {
          -webkit-appearance: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .pc-mobile-search-overlay,
          .pc-mobile-search-bar,
          .pc-mobile-search-suggestions {
            animation: none !important;
          }
        }
      `}</style>
      {/* ================= MOBILE BOTTOM GLASS NAV ================= */}
      <div
        className="d-block d-lg-none position-fixed bottom-0 start-0 w-100"
        style={{
          zIndex: 1050,
          transition: "transform 0.28s ease, opacity 0.28s ease",
          transform: mobileBottomHidden ? "translateY(120%)" : "translateY(0)",
          opacity: mobileBottomHidden ? 0 : 1,
          pointerEvents: mobileBottomHidden ? "none" : "auto",
        }}
      >
        <nav
          className="navbar px-3 py-2"
          style={{
            background: "#ffffff",
            backdropFilter: "blur(16px)",
            borderRadius: "0",
            boxShadow: "0 -6px 20px rgba(0,0,0,0.12)",
          }}
        >
          <ul className="navbar-nav flex-row justify-content-between w-100 px-3 text-center">

            <li>
              <Link className="nav-link" style={{ color: getNavColor("/") }} to="/">
                <i className="fas fa-house fs-5"></i>
              </Link>
            </li>

            <li className="position-relative">
              <Link className="nav-link" style={{ color: getNavColor("/cart") }} to="/cart">
                <i className="fas fa-cart-shopping fs-5"></i>
                {cartCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {cartCount}
                  </span>
                )}
              </Link>
            </li>

            <li>
              <button
                type="button"
                className="nav-link d-inline-flex align-items-center justify-content-center bg-transparent border-0 p-0"
                style={{
                  color: getNavColor("/products"),
                  lineHeight: 1,
                  width: 44,
                  height: 44,
                }}
                onClick={() => setMobileSearchOpen(true)}
                aria-label="Search products"
              >
                <i className="fas fa-magnifying-glass fs-5"></i>
              </button>
            </li>

            {/* <li>
              <button
                className="nav-link bg-transparent border-0 text-dark"
                onClick={() => setMobileMenu(true)}
              >
                <i className="fas fa-bars fs-5"></i>
              </button>
            </li> */}

            <li>
              <Link
                className="nav-link"
                style={{ color: getNavColor(isLoggedIn ? "/profile" : "/login") }}
                to={isLoggedIn ? "/profile" : "/login"}
                aria-label="User Profile"
              >
                {isLoggedIn ? (
                  <span
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #1F3C88, #4a6fdc)",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                    title={currentUser?.name || currentUser?.email || currentUser?.mobile || "Profile"}
                  >
                    {userInitial.toUpperCase()}
                  </span>
                ) : (
                  <i className="fas fa-user fs-5"></i>
                )}
              </Link>
            </li>

          </ul>
        </nav>
      </div>

      {/* ================= MOBILE SIDEBAR ================= */}
      {mobileMenu && (
        <>
          {/* Overlay */}
          <div
            onClick={() => setMobileMenu(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100vh",
              background: "rgba(0,0,0,0.4)",
              zIndex: 1999,
            }}
          ></div>

          {/* Sidebar */}
          {mobileMenu && (
            <div
              style={{
                position: "fixed",
                top: 0,
                right: 0,
                left: "auto",
                width: "90%",
                maxWidth: "380px",
                height: "100%",
                background: "#ffffff",
                color: "#351F72",
                fontWeight: 600,
                zIndex: 2000,
                padding: "20px",
                boxShadow: "-2px 0 20px rgba(0,0,0,0.2)",
                overflowY: "auto",
                transition: "0.3s ease",
                animation: "drawerInRight 0.3s ease",
              }}
            >
              {/* HEADER WITH LOGO */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <img
                  src={pccarelogo}
                  alt="Logo"
                  style={{ maxHeight: "40px" }}
                />

                <button
                  className="btn-close"
                  onClick={() => setMobileMenu(false)}
                  aria-label="Close menu"
                />
              </div>

              <ul className="list-unstyled">

                <li className="mb-3">
                  <Link
                    to="/"
                    onClick={() => setMobileMenu(false)}
                    style={{ color: getNavColor("/"), fontWeight: getNavWeight("/") }}
                  >
                    Home
                  </Link>
                </li>

                <li className="mb-3">
                  <Link
                    to="/products"
                    onClick={() => setMobileMenu(false)}
                    style={{ color: getNavColor("/products"), fontWeight: getNavWeight("/products") }}
                  >
                    Products
                  </Link>
                </li>

                <li className="mb-3">
                  <Link
                    to="/about"
                    onClick={() => setMobileMenu(false)}
                    style={{ color: getNavColor("/about"), fontWeight: getNavWeight("/about") }}
                  >
                    About
                  </Link>
                </li>
                <li className="mb-3">
                  <Link
                    to="/services"
                    onClick={() => setMobileMenu(false)}
                    style={{ color: getNavColor("/services"), fontWeight: getNavWeight("/services") }}
                  >
                    Services
                  </Link>
                </li>
<li className="mb-3">
                  <Link
                    to="/gallery"
                    onClick={() => setMobileMenu(false)}
                    style={{ color: getNavColor("/gallery"), fontWeight: getNavWeight("/gallery") }}
                  >
                    Gallery
                  </Link>
                </li>
                  <li className="mb-3">
                    <Link
                      to="/contact"
                      onClick={() => setMobileMenu(false)}
                      style={{ color: getNavColor("/contact"), fontWeight: getNavWeight("/contact") }}
                    >
                      Contact
                    </Link>
                  </li>

                  {!isLoggedIn && (
                    <li className="mb-3">
                      <Link
                        to="/login"
                        onClick={() => setMobileMenu(false)}
                        style={{ color: getNavColor("/login"), fontWeight: getNavWeight("/login") }}
                      >
                        Login
                      </Link>
                    </li>
                  )}

                  {isLoggedIn && (
                    <>
                      <li className="mb-3">
                        <Link
                          to="/profile"
                          onClick={() => setMobileMenu(false)}
                          style={{ color: getNavColor("/profile"), fontWeight: getNavWeight("/profile") }}
                        >
                          My Profile
                        </Link>
                      </li>
                      <li className="mb-3">
                        <button
                          type="button"
                          onClick={handleLogout}
                          className="bg-transparent border-0 p-0"
                          style={{ color: "#351F72", fontWeight: 600 }}
                        >
                          Logout
                        </button>
                      </li>
                    </>
                  )}

                {/* PRIVACY DROPDOWN */}
                <li className="mb-3">
                  <div
                    className="d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer", fontWeight: 600, color: isPolicyActive ? "#F3802C" : "#351F72" }}
                    onClick={() => setMobilePolicyOpen(!mobilePolicyOpen)}
                  >
                    Privacy & Policies
                    <i
                      className={`fas fa-chevron-down`}
                      style={{
                        transition: "0.3s",
                        transform: mobilePolicyOpen ? "rotate(180deg)" : "rotate(0deg)",
                        color: "#351F72",
                      }}
                    ></i>
                  </div>

                  {mobilePolicyOpen && (
                    <ul className="list-unstyled ps-3 mt-2">

                      <li><Link className="mb-2" to="/shipping-policy" onClick={() => setMobileMenu(false)} style={{ color: getNavColor("/shipping-policy"), fontWeight: getNavWeight("/shipping-policy") }}>Shipping Policy</Link></li>
                      <li><Link className="mb-2" to="/return-policy" onClick={() => setMobileMenu(false)} style={{ color: getNavColor("/return-policy"), fontWeight: getNavWeight("/return-policy") }}>Return Policy</Link></li>
                      <li><Link className="mb-2" to="/refund-policy" onClick={() => setMobileMenu(false)} style={{ color: getNavColor("/refund-policy"), fontWeight: getNavWeight("/refund-policy") }}>Refund & Cancellation Policy</Link></li>
                      <li><Link className="mb-2" to="/warranty-policy" onClick={() => setMobileMenu(false)} style={{ color: getNavColor("/warranty-policy"), fontWeight: getNavWeight("/warranty-policy") }}>Warranty Policy</Link></li>
                      <li><Link className="mb-2" to="/privacy-policy" onClick={() => setMobileMenu(false)} style={{ color: getNavColor("/privacy-policy"), fontWeight: getNavWeight("/privacy-policy") }}>Privacy Policy</Link></li>
                    </ul>
                  )}
                </li>

                {/* OTHER DROPDOWN */}
                <li className="mb-3">
                  <div
                    className="d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer", fontWeight: 600, color: isOtherActive ? "#F3802C" : "#351F72" }}
                    onClick={() => setMobileOtherOpen(!mobileOtherOpen)}
                  >
                    Other
                    <i
                      className="fas fa-chevron-down"
                      style={{
                        transition: "0.3s",
                        transform: mobileOtherOpen ? "rotate(180deg)" : "rotate(0deg)",
                        color: "#351F72",
                      }}
                    ></i>
                  </div>

                  {mobileOtherOpen && (
                    <ul className="list-unstyled ps-3 mt-2">
                      <li className="mb-2">
                        <Link to="/faq" onClick={() => setMobileMenu(false)} style={{ color: getNavColor("/faq"), fontWeight: getNavWeight("/faq") }}>FAQ</Link>
                      </li>
                      <li className="mb-2">
                        <Link to="/terms-conditions" onClick={() => setMobileMenu(false)} style={{ color: getNavColor("/terms-conditions"), fontWeight: getNavWeight("/terms-conditions") }}>Terms & Conditions</Link>
                      </li>
                    </ul>
                  )}
                </li>

              </ul>
              {/* ================= SIDEBAR BOTTOM SECTION ================= */}

              <hr style={{ margin: "20px 0" }} />

		              {/* ENQUIRY SECTION */}
		              <div className="mb-4">
		                <a
		                  href="tel:+91 9238434606"
		                  className="text-decoration-none"
		                >
		                  <div
		                    className="d-flex align-items-center gap-3 p-2"
		                    style={{
		                      borderRadius: "8px",

	                      transition: "0.2s",
	                    }}
		                  >
		                    <i
		                      className="fas fa-phone-volume"
		                      style={{ color: "#F3802C", fontSize: "18px" }}
		                    ></i>

		                    <div>
		                      <div style={{ fontSize: "13px", color: "#351F72" }}>
		                        For Enquiries
		                      </div>
	                      <div
	                        style={{
	                          fontWeight: "600",
	                          color: "#351F72",
	                        }}
	                      >
	                        +91 9238434606
	                      </div>
	                    </div>
	                  </div>
	                </a>
	              </div>

              {/* EMAIL SECTION */}
              <div className="mb-4">
                <a
                  href="mailto:info@pccarecomputers.com"
                  className="text-decoration-none"
                >
                  <div
                    className="d-flex align-items-center gap-3 p-2"
                    style={{
                      borderRadius: "8px",
                      transition: "0.2s",
                    }}
                  >
                    <i
                      className="fas fa-envelope"
                      style={{ color: "#F3802C", fontSize: "18px" }}
                    ></i>

                    <div>
                      <div style={{ fontSize: "13px", color: "#351F72" }}>
                        Email Us
                      </div>
                      <div
                        style={{
                          fontWeight: "600",
                          color: "#351F72",
                        }}
                      >
                        info@pccarecomputers.com
                      </div>
                    </div>
                  </div>
                </a>
              </div>

              {/* SOCIAL MEDIA */}
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#351F72",
                    fontWeight: "600",
                    marginBottom: "10px",
                  }}
                >
                  Follow Us
                </div>
                <div className="d-flex align-items-center gap-3">
                  <a
                    href="https://www.facebook.com/pccarejabalpur/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#351F72", fontSize: "18px" }}
                  >
                    <i className="fab fa-facebook-f"></i>
                  </a>

                  <a
                    href="https://www.instagram.com/pccare_jabalpur/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#351F72", fontSize: "18px" }}
                  >
                    <i className="fab fa-instagram"></i>
                  </a>

                  <a
                    href="https://www.youtube.com/@pccare_jabalpur"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#351F72", fontSize: "18px" }}
                  >
                    <i className="fab fa-youtube"></i>
                  </a>
                </div>
              </div>

            </div>

          )}

        </>
      )}

      {/* ================= DESKTOP HEADER ================= */}
      <header
        ref={headerRef}
        className="sticky-top"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1030,
        }}
      >

        {/* ================= TOP HEADER ================= */}
        <div
          className="py-3 border-bottom shadow-sm bg-white"
          style={{
            transition: "transform 0.28s ease, opacity 0.28s ease",
            transform: mobileTopHidden && isMobileView ? "translateY(-120%)" : "translateY(0)",
            opacity: mobileTopHidden && isMobileView ? 0 : 1,
            pointerEvents: mobileTopHidden && isMobileView ? "none" : "auto",
          }}
        >
          <div className="container">
            <div className="row align-items-center gy-3">

              {/* ================= MOBILE HEADER ================= */}
              <div
                className="col-12 d-flex d-lg-none justify-content-between align-items-center"
              >
                <Link to="/" aria-label="Home">
                  <img src={pccarelogo} alt="Logo" style={{ maxHeight: 45 }} loading="lazy" />
                </Link>

                <button
                  className="btn p-0"
                  onClick={() => setMobileMenu(true)}
                  aria-label="Open menu"
                >
                  <i className="fas fa-bars fs-3" style={{ color: "#351F72" }}></i>
                </button>
              </div>

              {/* ================= DESKTOP LOGO ================= */}
              <div className="col-lg-3 d-none d-lg-flex align-items-center">
                <Link to="/" aria-label="Home">
                  <img
                    src={pccarelogo}
                    alt="PC Care Logo"
                    loading="lazy"
                    style={{ maxHeight: 55 }}
                  />
                </Link>
              </div>

              {/* ================= DESKTOP SEARCH ================= */}
              <div className="col-lg-5 d-none d-lg-block">
                <form onSubmit={handleSearch} ref={searchWrapRef} style={{ position: "relative" }}>
                  <div
                    className={`d-flex align-items-center pc-search-wrap ${searchFocused ? "active" : ""}`}
                    style={{
                      background: "#fff",
                      borderRadius: "50px",
                      padding: "5px 5px 5px 20px",
                      boxShadow: "0 8px 20px rgba(53, 31, 114, 0.12)",
                      border: "2px solid #e6e0ff",
                    }}
                  >
                    <i
                      className="fas fa-search me-2 pc-search-icon"
                      style={{
                        color: "#351F72",
                      }}
                    ></i>

                    <div style={{ position: "relative", flex: 1 }}>
                      <TypewriterPlaceholder searchTerm={searchTerm} searchFocused={searchFocused} />
                      <input
                        className="pc-search-input"
                        type="search"
                        placeholder=""
                        aria-label="Search products"
                        value={searchTerm}
                        onFocus={openSuggestions}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          if (e.target.value.trim()) setSearchHint("");
                          setSuggestionsOpen(true);
                        }}
                        style={{
                          border: "none",
                          outline: "none",
                          width: "100%",
                          fontSize: "15px",
                          transform: searchFocused ? "translateX(1px)" : "translateX(0)",
                          background: "transparent",
                        }}
                      />
                    </div>

                    <button
                      className="pc-search-btn"
                      type="submit"
                      style={{
                        background: "#F3802C",
                        color: "#fff",
                        border: "none",
                        padding: "8px 22px",
                        borderRadius: "40px",
                        fontWeight: "600",
                        transform: searchFocused ? "scale(1.03)" : "scale(1)",
                      }}
                    >
                      Search
                    </button>
                  </div>

                  {suggestionsOpen && (searchFocused || searchTerm.trim()) && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 10px)",
                        left: 0,
                        right: 0,
                        background: "#fff",
                        border: "1px solid #e6ebfb",
                        borderRadius: 16,
                        boxShadow: "0 18px 38px rgba(0,0,0,0.14)",
                        zIndex: 1200,
                        overflow: "hidden",
                      }}
                    >
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          const q = searchTerm.trim();
                          if (!q) return;
                          setSuggestionsOpen(false);
                          navigate(`/products?q=${encodeURIComponent(q)}`);
                        }}
                        className="w-100 text-start border-0 bg-transparent"
                        style={{
                          padding: "12px 14px",
                          fontWeight: 700,
                          color: "#351F72",
                          borderBottom: "1px solid #f0f3ff",
                        }}
                      >
                        <i className="fas fa-search me-2" />
                        Search “{searchTerm.trim() || "products"}”
                      </button>

                      {productsIndexLoading && (
                        <div style={{ padding: "12px 14px", fontSize: 13, color: "#6b7280" }}>
                          Loading suggestions...
                        </div>
                      )}

                      {productsIndexError && !productsIndexLoading && (
                        <div style={{ padding: "12px 14px", fontSize: 13, color: "#dc2626" }}>
                          {productsIndexError}
                        </div>
                      )}

                      {!productsIndexLoading && !productsIndexError && suggestions.length === 0 && searchTerm.trim() && (
                        <div style={{ padding: "12px 14px", fontSize: 13, color: "#6b7280" }}>
                          No matching products.
                        </div>
                      )}

                      {!productsIndexLoading &&
                        !productsIndexError &&
                        suggestions.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => goToProduct(p.id)}
                            className="w-100 text-start border-0 bg-transparent"
                            style={{
                              padding: "12px 14px",
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <span
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 10,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#f1f4ff",
                                color: "#1F3C88",
                                flex: "0 0 auto",
                                overflow: "hidden",
                              }}
                            >
                              {p.image ? (
                                <img
                                  src={p.image}
                                  alt={p.name}
                                  loading="lazy"
                                  onError={(e) => {
                                    e.currentTarget.src = pccarelogo;
                                  }}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              ) : (
                                <i className="fas fa-laptop" />
                              )}
                            </span>

                            <span style={{ minWidth: 0, flex: 1 }}>
                              <div
                                style={{
                                  fontWeight: 700,
                                  color: "#1F3C88",
                                  fontSize: 14,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={p.name}
                              >
                                {p.name}
                              </div>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>
                                {[p.brand, p.processor].filter(Boolean).join(" • ") || "View details"}
                              </div>
                            </span>
                          </button>
                        ))}
                    </div>
                  )}

                  {searchHint && (
                    <p className="mb-0 mt-2 ms-2 text-danger small">{searchHint}</p>
                  )}
                </form>
              </div>

	              {/* ================= ENQUIRY BUTTON ================= */}
              <div className="col-lg-2 d-none d-lg-flex align-items-center">
                <Link
                  to="/contact"
                  className="text-decoration-none w-100 d-flex align-items-center justify-content-center gap-2"
                  style={{
                    padding: "2px 5px",
                    transition: "0.3s",
                    cursor: "pointer",
                  }}
                >
                  <i className="fas fa-phone-volume" style={{ color: "#F3802C" }}></i>

                  <span
                    style={{
                      fontWeight: "600",
                      color: "#351F72",
                      fontSize: "14px",
                    }}
                  >
                    Contact Now
                  </span>
                </Link>
              </div>

              {/* ================= DESKTOP ICONS ================= */}
              <div className="col-lg-2 d-none d-lg-flex justify-content-end align-items-center gap-4">

                {/* Cart */}
                <Link to="/cart" className="position-relative fs-5" style={{ color: getNavColor("/cart") }}>
                  <i className="fas fa-shopping-cart pc-icon-anim"></i>
                  {cartCount > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                      {cartCount}
                    </span>
                  )}
                </Link>

                {/* Login */}
                <Link to={isLoggedIn ? "/profile" : "/login"} className="fs-5" style={{ color: getNavColor(isLoggedIn ? "/profile" : "/login") }}>
                  {isLoggedIn ? (
                    <span
                      className="d-inline-flex align-items-center justify-content-center"
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #1F3C88, #4a6fdc)",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 14,
                      }}
                      title={currentUser?.name || currentUser?.email || currentUser?.mobile || "Profile"}
                    >
                      {userInitial.toUpperCase()}
                    </span>
                  ) : (
                    <i className="fas fa-user pc-icon-anim"></i>
                  )}
                </Link>

                {/* Location Dropdown */}
                <div className="position-relative" ref={locationRef}>
                  <button
                    onClick={() => setLocationOpen((prev) => !prev)}
                    className="btn p-0 fs-5"
                    style={{ color: "#351F72" }}
                    aria-label="Our branches"
                  >
                    <i className="fas fa-location-dot pc-icon-anim"></i>
                  </button>
                  {locationOpen && (
                    <div
                      className="position-absolute end-0 mt-2 bg-white rounded-2 shadow-lg border"
                      style={{ minWidth: 320, zIndex: 1050, padding: "12px 16px" }}
                    >
                      <p className="fw-bold mb-2 text-dark" style={{ fontSize: 13 }}>Our Branches</p>
                      <a
                        href={mapUrlJabalpur}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-flex align-items-start gap-2 text-decoration-none p-2 rounded mb-1"
                        style={{ color: "#333", fontSize: 13 }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <i className="fas fa-map-marker-alt mt-1" style={{ color: "#E63946" }}></i>
                        <div>
                          <strong>Jabalpur</strong>
                          <p className="mb-0 text-muted" style={{ fontSize: 12, lineHeight: 1.4 }}>
                            Pc Care Computer Laptop Repair Center
                          </p>
                        </div>
                      </a>
                      <a
                        href={mapUrlBhopal}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-flex align-items-start gap-2 text-decoration-none p-2 rounded"
                        style={{ color: "#333", fontSize: 13 }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <i className="fas fa-map-marker-alt mt-1" style={{ color: "#E63946" }}></i>
                        <div>
                          <strong>Bhopal</strong>
                          <p className="mb-0 text-muted" style={{ fontSize: 12, lineHeight: 1.4 }}>
                            2nd Floor, 80-B, Kasturba Nagar, Near Chetak Bridge, Above Ayurvedic Clinic, Bhopal
                          </p>
                        </div>
                      </a>
                    </div>
                  )}
                </div>

                {/* Desktop Hamburger */}
                <button
                  className="btn p-0"
                  onClick={() => setMobileMenu(true)}
                  aria-label="Open menu"
                >
                  <i className="fas fa-bars fs-4" style={{ color: "#351F72" }}></i>
                </button>

              </div>

            </div>
          </div>
        </div>

      </header>

      {/* ================= MOBILE SEARCH MODAL ================= */}
      {mobileSearchOpen && (
        <div
          className="d-block d-lg-none pc-mobile-search-overlay"
          onClick={() => setMobileSearchOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "transparent",
            zIndex: 2200,
          }}
        >
          <div
            className="pc-mobile-search-bar"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              left: 8,
              right: 8,
              bottom: mobileBottomHidden ? 0 : 62,
              maxWidth: 680,
              margin: "0 auto",
              background: "transparent",
              overflow: "visible",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column-reverse", gap: 10, padding: 12 }}>
            <form onSubmit={handleSearch} style={{ padding: 0 }}>
              <div
                className="d-flex align-items-center"
                style={{
                  background: "#f7f8ff",
                  border: "2px solid #e2dcff",
                  borderRadius: 14,
                  padding: "10px 12px",
                  gap: 10,
                }}
              >
                <i className="fas fa-magnifying-glass" style={{ color: "#351F72" }} />
                <input
                  ref={mobileSearchRef}
                  type="search"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setSearchHint("");
                  }}
                  placeholder="Search laptop, brand, i5..."
                  aria-label="Search products mobile"
                  style={{
                    border: "none",
                    outline: "none",
                    flex: 1,
                    background: "transparent",
                    fontSize: 15,
                    fontWeight: 600,
                    color: "#1f3c88",
                    minWidth: 0,
                  }}
                />
                <button
                  type="submit"
                  aria-label="Search"
                  style={{
                    border: "none",
                    background: "#F3802C",
                    color: "#fff",
                    fontWeight: 800,
                    width: 42,
                    height: 42,
                    borderRadius: 14,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                  }}
                >
                  <i className="fas fa-arrow-right" />
                </button>
              </div>

              {searchHint && (
                <p className="mb-0 mt-2 text-danger small">{searchHint}</p>
              )}
            </form>

            {searchTerm.trim() && (
            <div
              className="pc-mobile-search-suggestions"
              style={{
                background: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(14px)",
                border: "1px solid #e3e9ff",
                borderRadius: 16,
                boxShadow: "0 14px 30px rgba(0,0,0,0.14)",
                overflow: "hidden",
              }}
            >
              {false && (
              <button
                type="button"
                onClick={() => {
                  const q = searchTerm.trim();
                  if (!q) return;
                  setMobileSearchOpen(false);
                  navigate(`/products?q=${encodeURIComponent(q)}`);
                }}
                className="w-100 text-start border-0 bg-transparent"
                style={{
                  display: "none",
                  padding: "12px 14px",
                  fontWeight: 800,
                  color: "#351F72",
                  borderBottom: "1px solid #f3f5ff",
                }}
              >
                <i className="fas fa-search me-2" />
                Search “{searchTerm.trim() || "products"}”
              </button>
              )}

              <div style={{ maxHeight: "52vh", overflowY: "auto" }}>
                {productsIndexLoading && (
                  <div style={{ padding: "12px 14px", fontSize: 13, color: "#6b7280" }}>
                    Loading suggestions...
                  </div>
                )}

                {productsIndexError && !productsIndexLoading && (
                  <div style={{ padding: "12px 14px", fontSize: 13, color: "#dc2626" }}>
                    {productsIndexError}
                  </div>
                )}

                {!productsIndexLoading && !productsIndexError && suggestions.length === 0 && searchTerm.trim() && (
                  <div style={{ padding: "12px 14px", fontSize: 13, color: "#6b7280" }}>
                    No matching products.
                  </div>
                )}

                {!productsIndexLoading &&
                  !productsIndexError &&
                  suggestions.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => goToProduct(p.id)}
                      className="w-100 text-start border-0 bg-transparent"
                      style={{
                        padding: "12px 14px",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        borderBottom: "1px solid #f7f8ff",
                      }}
                    >
                      <span
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 12,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#f1f4ff",
                          color: "#1F3C88",
                          flex: "0 0 auto",
                          overflow: "hidden",
                        }}
                      >
                        {p.image ? (
                          <img
                            src={p.image}
                            alt={p.name}
                            onError={(e) => {
                              e.currentTarget.src = pccarelogo;
                            }}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <i className="fas fa-laptop" />
                        )}
                      </span>

                      <span style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 800,
                            color: "#1F3C88",
                            fontSize: 14,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={p.name}
                        >
                          {p.name}
                        </div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                          {[p.brand, p.processor].filter(Boolean).join(" • ") || "View details"}
                        </div>
                      </span>
                    </button>
                  ))}
              </div>
            </div>
            )}
            </div>
          </div>
        </div>
      )}

    </>
  );


};

export default Navbar;
